import { useEffect, useState } from "react";
import { Megaphone } from "lucide-react";
import AdImage from "@/components/AdImage";
import api from "@/lib/api";

// Auto-rotating sponsor carousel (zone="showcase"). Self-manages its column width
// from the admin size preset; collapses to full width and stacks on mobile.
const WIDTH = {
  small: "lg:w-1/5",
  medium: "lg:w-1/4",
  large: "lg:w-1/3",
};

export default function AdShowcase({ orientation = "vertical" }) {
  const [ads, setAds] = useState([]);
  const [size, setSize] = useState("large");
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    let alive = true;
    Promise.all([
      api.get("/config/ads", { params: { zone: "showcase" } }).then((r) => r.data).catch(() => []),
      api.get("/config/ad-settings").then((r) => r.data).catch(() => ({ showcase_size: "large" })),
    ]).then(([a, s]) => {
      if (!alive) return;
      setAds(a || []);
      setSize(s?.showcase_size || "large");
    });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (ads.length <= 1) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % ads.length), 4500);
    return () => clearInterval(t);
  }, [ads.length]);

  if (ads.length === 0) return null;
  const ad = ads[idx % ads.length];

  const inner = (
    <div className="relative rounded-3xl glow-border overflow-hidden h-full flex flex-col" style={{ background: "var(--surface)" }} data-testid={`showcase-ad-${ad.ad_id}`}>
      <div className="flex items-center gap-2 px-4 pt-3">
        <Megaphone className="w-3.5 h-3.5 text-primary-c" />
        <span className="text-[10px] mono uppercase tracking-[0.3em] text-primary-c">Sponsor</span>
      </div>
      <div className="flex-1 flex items-center justify-center p-3 min-h-[160px]">
        {ad.image_url
          ? <AdImage src={ad.image_url} alt={ad.title} className="max-h-full max-w-full w-full h-full object-cover rounded-2xl" testid="showcase-img" />
          : <div className="w-full h-full min-h-[140px] rounded-2xl flex items-center justify-center" style={{ background: "var(--bg)" }}><Megaphone className="w-8 h-8 text-primary-c opacity-40" /></div>}
      </div>
      <div className="px-4 pb-4">
        <div className="font-display font-bold text-lg leading-tight" data-testid="showcase-title">{ad.title}</div>
        {ad.subtitle && <div className="text-xs mt-1" style={{ color: "var(--text-2)" }}>{ad.subtitle}</div>}
      </div>
      {ads.length > 1 && (
        <div className="flex justify-center gap-1.5 pb-3" data-testid="showcase-dots">
          {ads.map((_, i) => (
            <span key={i} className="w-1.5 h-1.5 rounded-full transition-colors" style={{ background: i === idx % ads.length ? "var(--primary)" : "var(--border)" }} />
          ))}
        </div>
      )}
    </div>
  );

  const width = orientation === "vertical" ? `w-full ${WIDTH[size] || WIDTH.large} shrink-0` : "w-full";
  const body = ad.link ? (
    <a href={ad.link} target="_blank" rel="noreferrer" className="block h-full">{inner}</a>
  ) : inner;

  return <aside className={width} data-testid="ad-showcase">{body}</aside>;
}
